/**
 * DbLoginPage - 登录页面业务组件
 *
 * 提供多种登录方式：
 * - 账号密码登录
 * - 手机号一键登录
 * - 短信验证码登录
 * - 第三方登录（微信、支付宝等）
 *
 * 支持通过 props 配置开启/关闭各种登录功能
 */

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import {
  DbLoginPageProps,
  DbLoginPageRef,
  LoginType,
  ThirdPartyPlatform,
} from './DbLoginPage.types';
import { createStyles, THIRD_PARTY_COLORS } from './DbLoginPage.styles';
import DbInput from '../DbInput';
import DbIcon from '../DbIcon';

// 默认第三方登录配置
const DEFAULT_THIRD_PARTY_LOGINS: Array<{ platform: ThirdPartyPlatform; title: string; icon?: string; iconFamily?: string; onPress?: () => void }> = [
  { platform: 'wechat', title: '微信登录' },
  { platform: 'alipay', title: '支付宝登录' },
];

// 第三方登录图标映射
const THIRD_PARTY_ICONS: Record<ThirdPartyPlatform, { name: string; family: string }> = {
  wechat: { name: 'wechat', family: 'FontAwesome' },
  alipay: { name: 'alipay-circle', family: 'AntDesign' },
  qq: { name: 'qq', family: 'FontAwesome' },
  weibo: { name: 'weibo', family: 'FontAwesome' },
};

const DbLoginPage = forwardRef<DbLoginPageRef, DbLoginPageProps>((props, ref) => {
  const {
    // 基础配置
    showLogo = true,
    logoSource,
    appName,
    title = '欢迎登录',
    subtitle,
    // 功能开关
    enableAccountLogin = true,
    enablePhoneLogin = true,
    enableSmsLogin = true,
    enableThirdPartyLogin = true,
    showRegisterLink = true,
    showForgotPassword = true,
    // 默认配置
    defaultLoginType = 'account',
    defaultAccount = '',
    defaultPassword = '',
    accountPlaceholder = '请输入账号/手机号/邮箱',
    passwordPlaceholder = '请输入密码',
    phonePlaceholder = '请输入手机号',
    codePlaceholder = '请输入验证码',
    loginButtonText = '登录',
    // 第三方登录
    thirdPartyLogins = DEFAULT_THIRD_PARTY_LOGINS,
    thirdPartyTitle = '其他登录方式',
    // 事件回调
    onAccountLogin,
    onPhoneLogin,
    onSmsLogin,
    onSendCode,
    onRegister,
    onForgotPassword,
    onUserAgreement,
    onPrivacyPolicy,
    // 自定义渲染
    renderLogo,
    renderHeader,
    renderFooter,
    renderAgreement,
    // 样式配置
    style,
    contentStyle,
    titleStyle,
    subtitleStyle,
    loginButtonStyle,
    loginButtonTextStyle,
    tabBarStyle,
    backgroundColor = '#fff',
    primaryColor = '#007AFF',
    // 高级配置
    autoFocus = false,
    codeCountdown = 60,
    showPasswordStrength = false,
    agreeToTerms: controlledAgree,
    onAgreeChange,
    requireAgreement = true,
  } = props;

  const styles = useMemo(() => createStyles(primaryColor), [primaryColor]);

  // 状态
  const [loginType, setLoginType] = useState<LoginType>(defaultLoginType);
  const [account, setAccount] = useState(defaultAccount);
  const [password, setPassword] = useState(defaultPassword);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(controlledAgree ?? false);
  const [countdown, setCountdown] = useState(0);

  // Refs
  const accountInputRef = useRef<TextInput>(null);
  const passwordInputRef = useRef<TextInput>(null);
  const phoneInputRef = useRef<TextInput>(null);
  const codeInputRef = useRef<TextInput>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 计算可用的登录类型
  const availableLoginTypes = useMemo(() => {
    const types: LoginType[] = [];
    if (enableAccountLogin) types.push('account');
    if (enablePhoneLogin || enableSmsLogin) types.push('phone');
    return types;
  }, [enableAccountLogin, enablePhoneLogin, enableSmsLogin]);

  // 同步受控的协议勾选状态
  useEffect(() => {
    if (controlledAgree !== undefined) {
      setAgreed(controlledAgree);
    }
  }, [controlledAgree]);

  // 清理倒计时定时器
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [countdown]);

  // 协议勾选处理
  const handleAgreeChange = useCallback(() => {
    const newValue = !agreed;
    setAgreed(newValue);
    onAgreeChange?.(newValue);
  }, [agreed, onAgreeChange]);

  // 发送验证码
  const handleSendCode = useCallback(async () => {
    if (!phone) {
      Alert.alert('提示', '请输入手机号');
      return;
    }
    if (!/^1\d{10}$/.test(phone)) {
      Alert.alert('提示', '请输入正确的手机号');
      return;
    }

    if (onSendCode) {
      const success = await onSendCode(phone);
      if (success) {
        setCountdown(codeCountdown);
      }
    } else {
      // 模拟发送成功
      setCountdown(codeCountdown);
      Alert.alert('提示', '验证码已发送');
    }
  }, [phone, onSendCode, codeCountdown]);

  // 登录处理
  const handleLogin = useCallback(async () => {
    // 检查协议
    if (requireAgreement && !agreed) {
      Alert.alert('提示', '请先阅读并同意用户协议和隐私政策');
      return;
    }

    setLoading(true);

    try {
      let result;

      if (loginType === 'account') {
        if (!account || !password) {
          Alert.alert('提示', '请输入账号和密码');
          setLoading(false);
          return;
        }
        result = await onAccountLogin?.({ account, password });
      } else if (loginType === 'phone') {
        if (!phone) {
          Alert.alert('提示', '请输入手机号');
          setLoading(false);
          return;
        }
        
        if (enableSmsLogin && code) {
          // 短信验证码登录
          result = await onSmsLogin?.({ phone, code });
        } else if (enablePhoneLogin) {
          // 手机号一键登录
          result = await onPhoneLogin?.({ phone });
        }
      }

      if (result && !result.success) {
        Alert.alert('登录失败', result.message || '请检查账号密码');
      }
    } catch (error: any) {
      Alert.alert('登录失败', error.message || '网络异常，请重试');
    } finally {
      setLoading(false);
    }
  }, [
    loginType,
    account,
    password,
    phone,
    code,
    agreed,
    requireAgreement,
    enablePhoneLogin,
    enableSmsLogin,
    onAccountLogin,
    onPhoneLogin,
    onSmsLogin,
  ]);

  // 计算密码强度
  const passwordStrength = useMemo(() => {
    if (!showPasswordStrength || !password) return 0;
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return Math.min(strength, 3);
  }, [password, showPasswordStrength]);

  // 暴露方法
  useImperativeHandle(ref, () => ({
    reset: () => {
      setAccount('');
      setPassword('');
      setPhone('');
      setCode('');
    },
    focus: (field) => {
      switch (field) {
        case 'account':
          accountInputRef.current?.focus();
          break;
        case 'password':
          passwordInputRef.current?.focus();
          break;
        case 'phone':
          phoneInputRef.current?.focus();
          break;
        case 'code':
          codeInputRef.current?.focus();
          break;
      }
    },
    getFormData: () => {
      if (loginType === 'account') {
        return { account, password };
      }
      return { phone, code };
    },
    setLoginType,
    triggerLogin: handleLogin,
  }));

  // ==================== 渲染 Logo ====================
  const renderLogoSection = () => {
    if (!showLogo) return null;

    if (renderLogo) {
      return renderLogo();
    }

    return (
      <View style={styles.logoContainer}>
        {logoSource ? (
          <Image source={logoSource} style={styles.logo} resizeMode="contain" />
        ) : (
          <View style={styles.logoPlaceholder}>
            <DbIcon name="apps" family="Ionicons" size={40} color="#fff" />
          </View>
        )}
        {appName && <Text style={styles.appName}>{appName}</Text>}
      </View>
    );
  };

  // ==================== 渲染标题 ====================
  const renderHeaderSection = () => {
    if (renderHeader) {
      return renderHeader();
    }

    return (
      <View style={styles.headerContainer}>
        <Text style={[styles.title, titleStyle]}>{title}</Text>
        {subtitle && <Text style={[styles.subtitle, subtitleStyle]}>{subtitle}</Text>}
      </View>
    );
  };

  // ==================== 渲染 Tab 栏 ====================
  const renderTabBar = () => {
    if (availableLoginTypes.length <= 1) return null;

    return (
      <View style={[styles.tabBar, tabBarStyle]}>
        {enableAccountLogin && (
          <TouchableOpacity
            style={[styles.tabItem, loginType === 'account' && styles.tabItemActive]}
            onPress={() => setLoginType('account')}
          >
            <Text style={[styles.tabText, loginType === 'account' && styles.tabTextActive]}>
              账号登录
            </Text>
          </TouchableOpacity>
        )}
        {(enablePhoneLogin || enableSmsLogin) && (
          <TouchableOpacity
            style={[styles.tabItem, loginType === 'phone' && styles.tabItemActive]}
            onPress={() => setLoginType('phone')}
          >
            <Text style={[styles.tabText, loginType === 'phone' && styles.tabTextActive]}>
              手机登录
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // ==================== 渲染账号密码表单 ====================
  const renderAccountForm = () => {
    return (
      <View style={styles.formContainer}>
        <View style={styles.inputWrapper}>
          <DbInput
            placeholder={accountPlaceholder}
            value={account}
            onChangeText={setAccount}
            leftIcon={{ name: 'person-outline', family: 'Ionicons' }}
            showClear
            shape="round"
            autoFocus={autoFocus && loginType === 'account'}
          />
        </View>

        <View style={styles.inputWrapper}>
          <DbInput
            type="password"
            placeholder={passwordPlaceholder}
            value={password}
            onChangeText={setPassword}
            leftIcon={{ name: 'lock-closed-outline', family: 'Ionicons' }}
            showPasswordToggle
            shape="round"
          />
        </View>

        {/* 密码强度指示 */}
        {showPasswordStrength && password && (
          <View style={styles.passwordStrength}>
            {[1, 2, 3].map((level) => (
              <View
                key={level}
                style={[
                  styles.strengthBar,
                  passwordStrength >= level && (
                    passwordStrength === 1 ? styles.strengthBarWeak :
                    passwordStrength === 2 ? styles.strengthBarMedium :
                    styles.strengthBarActive
                  ),
                ]}
              />
            ))}
            <Text style={styles.strengthText}>
              {passwordStrength === 0 ? '' :
               passwordStrength === 1 ? '弱' :
               passwordStrength === 2 ? '中' : '强'}
            </Text>
          </View>
        )}

        {/* 忘记密码 */}
        {showForgotPassword && (
          <TouchableOpacity style={styles.forgotContainer} onPress={onForgotPassword}>
            <Text style={styles.forgotText}>忘记密码?</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // ==================== 渲染手机号表单 ====================
  const renderPhoneForm = () => {
    return (
      <View style={styles.formContainer}>
        <View style={styles.inputWrapper}>
          <DbInput
            type="phone"
            placeholder={phonePlaceholder}
            value={phone}
            onChangeText={setPhone}
            leftIcon={{ name: 'call-outline', family: 'Ionicons' }}
            phonePrefix="+86"
            showClear
            shape="round"
            autoFocus={autoFocus && loginType === 'phone'}
          />
        </View>

        {/* 验证码输入（仅短信验证码登录时显示） */}
        {enableSmsLogin && (
          <View style={styles.inputWrapper}>
            <View style={styles.inputRow}>
              <View style={styles.codeInput}>
                <DbInput
                  placeholder={codePlaceholder}
                  value={code}
                  onChangeText={setCode}
                  leftIcon={{ name: 'chatbubble-outline', family: 'Ionicons' }}
                  maxLength={6}
                  keyboardType="number-pad"
                  shape="round"
                />
              </View>
              <TouchableOpacity
                style={[
                  styles.codeButton,
                  countdown > 0 && styles.codeButtonDisabled,
                ]}
                onPress={handleSendCode}
                disabled={countdown > 0}
              >
                <Text style={styles.codeButtonText}>
                  {countdown > 0 ? `${countdown}s` : '获取验证码'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  };

  // ==================== 渲染协议 ====================
  const renderAgreementSection = () => {
    if (renderAgreement) {
      return renderAgreement();
    }

    return (
      <View style={styles.agreementContainer}>
        <TouchableOpacity
          style={[styles.checkbox, agreed && styles.checkboxChecked]}
          onPress={handleAgreeChange}
        >
          {agreed && <DbIcon name="checkmark" family="Ionicons" size={12} color="#fff" />}
        </TouchableOpacity>
        <Text style={styles.agreementText}>
          我已阅读并同意
          <Text style={styles.agreementLink} onPress={onUserAgreement}>
            《用户协议》
          </Text>
          和
          <Text style={styles.agreementLink} onPress={onPrivacyPolicy}>
            《隐私政策》
          </Text>
        </Text>
      </View>
    );
  };

  // ==================== 渲染登录按钮 ====================
  const renderLoginButton = () => {
    const isDisabled = loading || (requireAgreement && !agreed);

    return (
      <TouchableOpacity
        style={[
          styles.loginButton,
          isDisabled && styles.loginButtonDisabled,
          loginButtonStyle,
        ]}
        onPress={handleLogin}
        disabled={isDisabled}
        activeOpacity={0.8}
      >
        {loading ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <Text style={[styles.loginButtonText, loginButtonTextStyle]}>
            {loginButtonText}
          </Text>
        )}
      </TouchableOpacity>
    );
  };

  // ==================== 渲染第三方登录 ====================
  const renderThirdPartySection = () => {
    if (!enableThirdPartyLogin || thirdPartyLogins.length === 0) return null;

    return (
      <View style={styles.thirdPartyContainer}>
        <View style={styles.dividerContainer}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>{thirdPartyTitle}</Text>
          <View style={styles.dividerLine} />
        </View>

        <View style={styles.thirdPartyButtons}>
          {thirdPartyLogins.map((config) => {
            const iconConfig = THIRD_PARTY_ICONS[config.platform];
            const iconColor = THIRD_PARTY_COLORS[config.platform];

            return (
              <TouchableOpacity
                key={config.platform}
                style={styles.thirdPartyButton}
                onPress={config.onPress}
              >
                <DbIcon
                  name={config.icon || iconConfig?.name || 'link'}
                  family={(config.iconFamily || iconConfig?.family || 'Ionicons') as any}
                  size={28}
                  color={iconColor}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // ==================== 渲染底部 ====================
  const renderFooterSection = () => {
    if (renderFooter) {
      return renderFooter();
    }

    if (!showRegisterLink) return null;

    return (
      <View style={styles.footerContainer}>
        <Text style={styles.footerText}>
          还没有账号?{' '}
          <Text style={styles.registerLink} onPress={onRegister}>
            立即注册
          </Text>
        </Text>
      </View>
    );
  };

  // ==================== 主渲染 ====================
  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor }, style]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.content, contentStyle]}>
          {renderLogoSection()}
          {renderHeaderSection()}
          {renderTabBar()}
          
          {/* 表单区域 */}
          {loginType === 'account' && renderAccountForm()}
          {loginType === 'phone' && renderPhoneForm()}
          
          {/* 协议 */}
          {renderAgreementSection()}
          
          {/* 登录按钮 */}
          {renderLoginButton()}
          
          {/* 第三方登录 */}
          {renderThirdPartySection()}
          
          {/* 底部 */}
          {renderFooterSection()}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
});

DbLoginPage.displayName = 'DbLoginPage';

export { DbLoginPage };
export type {
  DbLoginPageProps,
  DbLoginPageRef,
  LoginType,
  ThirdPartyPlatform,
  ThirdPartyLoginConfig,
  AccountLoginData,
  PhoneLoginData,
  LoginResult,
} from './DbLoginPage.types';
